import React, { useEffect, useRef, useState } from "react";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Alert from "../Alert";
import ActionButton from "../ActionButton";
import { useCopyFeedback } from "../../hooks/useCopyFeedback";
// tracking-01 docs/COOLDOWN-MANAGEMENT.md rule 1 : "NEVER implement
// custom cooldown state in page components". This panel used to hold
// its own deadline map and tick ; the hook owns both now.
import { useMultiCooldown, cooldownLabel } from "../../hooks/useMultiCooldown";
import { downloadFile } from "../../utils/helpers";
// One owner of the labelled value-row idiom (2026-08-15 design
// overhaul). Local aliases keep this file's call sites stable.
import {
  ValueRow as ChallengeValueRow,
  ValueRowCopyButton as ChallengeCopyButton,
} from "./ValueRow";
// 2026-08-14 go-public review : the cooldown discriminator is read
// from the shared tier so this branch and the server's producer can
// never disagree on the literal.
import {
  TRIGGER_COOLDOWN_CODE,
  CHECK_COOLDOWN_CODE,
  TRIGGER_COOLDOWN_MS,
  CHECK_COOLDOWN_MS,
  // One owner of the wildcard "*." prefix (2026-08-22) : this panel
  // rebuilt it by hand in three places and Step 3's already-authorized
  // notice did not rebuild it at all, which is the defect that found
  // this. See its JSDoc.
  challengeDomainLabel,
  // 2026-08-14 go-public review : one owner of the CA error-detail
  // join, shared with the server's getChallengeDetails passthrough.
  joinChallengeErrorDetails,
} from "../../../shared/issuance/config";
// 2026-07-03 PR #76 sixth-pass review (Finding 4b) : sanitizeFilename is
// deliberately NOT imported here - the HTTP-01 challenge download must
// use the token byte-for-byte (see handleDownloadHttpFile for the full
// RFC 8555 §8.1/§8.3 rationale). It is the one filename that correctly
// bypasses the shared sanitiser, because the sanitiser would mangle a
// valid base64url token and break Domain Validation.
import {
  apiTriggerValidation,
  apiCheckChallenge,
  apiGetChallenges,
  ApiError,
  SHORT_CALL_TIMEOUT_MS,
  withTimeoutSignal,
} from "../../issuance/apiClient";

/**
 * ChallengePanel - render one authorization's challenge details as ONE
 * numbered, customer-driven sequence and drive the two SEPARATE manual
 * validation actions.
 *
 * 2026-07-04 Step 3 presentation redesign (operator direction : one
 * method at a time, numbered sequence, no polling). The previous layout
 * rendered the DNS-01 and HTTP-01 columns side by side, each with its
 * own trigger button, plus a shared check button below - the operator's
 * verdict was "buttons all over the place with no indication of what to
 * do with them". The panel now shows a two-option method selector
 * (DNS-01 selected by default ; wildcard domains render NO selector
 * because only DNS-01 is permitted for them) and walks the customer
 * through a single numbered sequence for the SELECTED method only :
 *
 *   1 : DNS Record to Create / File to Upload - the challenge data
 *       (exactly the record / file fields shown before the redesign).
 *   2 : Validation Request and Result - the two actions, side by side
 *       as ONE ask-then-read pair :
 *         Request Validation -> apiTriggerValidation (POST {} to the
 *           challenge URL). Asks Sectigo® to validate. Press ONCE
 *           after deploying the record or file ; the server
 *           rate-limits rapid re-triggers.
 *         Check Validation Status -> apiCheckChallenge (read-only
 *           status poll). Asks "is it done yet?" without
 *           re-triggering. Safe to press repeatedly - the customer
 *           presses again until it reads valid.
 *
 * Post-pivot architecture : all crypto and ACME I/O happens on the
 * Trustico® server. There is NO automatic polling - every Certificate
 * Authority interaction stays an explicit customer click. Matching the
 * working cPanel/acme.sh flow, the trigger and the status check remain
 * two distinct operations (fusing them wedged Sectigo® challenges in
 * "processing" - Sectigo® validates once per trigger and a re-trigger
 * before that attempt finishes resets it).
 *
 * Props :
 *   sessionId    - server session id
 *   entry        - one element of the /api/issuance/.../challenges
 *                  array. Shape :
 *                  {
 *                    authUrl, domain, wildcard, status,
 *                    errorDetail (string | null, sent only when status is "invalid"),
 *                    dns:  { challengeUrl, recordName, recordType, recordValue, status, token,
 *                            triggerCooldownRemainingMs, checkCooldownRemainingMs } | null,
 *                    http: { challengeUrl, filePath, fileContents, status, token,
 *                            triggerCooldownRemainingMs, checkCooldownRemainingMs } | null,
 *                  }
 *   onValidated  - called with the authorization URL once status
 *                  flips to "valid"
 *   onInvalid    - called with the authorization URL when a live
 *                  check reads terminally "invalid" (2026-08-14
 *                  go-public review : lets the parent retire the
 *                  numbered-step instructions when every panel has
 *                  withdrawn its sequence - the load-time status the
 *                  parent's gate reads never changes after mount)
 */

/**
 * The no-detail fallback for a TERMINALLY INVALID authorization -
 * one constant for BOTH emission sites (the mount-time seed and
 * runCheck's live invalid branch) : two hand-written copies is how
 * one site ends up with a "VALIDATION FAILED" body that restates
 * the Alert title (2026-08-14 go-public review). The wording
 * ADDS information : when the CA supplies no detail, saying so is
 * the only honest content.
 */
const NO_CA_REASON_FALLBACK =
  "The Certificate Authority (CA) did not give a reason for the rejection.";

/**
 * StepHeading - one numbered heading inside the panel's sequence.
 *
 * Renders a small numbered disc (a span holding the number) beside an
 * h4 holding the Title Case title, with the optional description
 * sentence under the title. The number sits OUTSIDE the h4, so it is
 * not part of the heading's accessible name : a heading is found by
 * its title alone (for example "DNS Record to Create"). The disc is
 * real text, not aria-hidden, so assistive technology still reads the
 * position in order just before the heading. The comment inside the
 * function records the 2026-08-15 design overhaul this shape comes
 * from.
 */
function StepHeading({ number, children, description }) {
  return (
    <div className="flex gap-3">
      {/* The number is REAL text (not aria-hidden) so assistive
          technology announces the position as part of the reading
          order, the same guarantee the retired plain-text "N : "
          prefix gave. 2026-08-15 design overhaul (operator-approved
          mockup) : the position renders as a small numbered disc and
          each heading carries its ONE description sentence here, so
          the guidance lives beside the control it describes instead
          of repeating across the page. */}
      <span className="flex-none w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-bold flex items-center justify-center translate-y-0.5">
        {number}
      </span>
      <div className="min-w-0">
        <h4 className="text-base font-semibold text-heading">{children}</h4>
        {description && <p className="text-body mt-2">{description}</p>}
      </div>
    </div>
  );
}

function ChallengePanel({ sessionId, entry, onValidated, onInvalid }) {
  const { domain, wildcard, authUrl, dns, http } = entry;

  // idle | checking | pending | valid | invalid - the last READ status.
  // Mount-time mapping covers the FULL terminal set (2026-08-14
  // go-public review, "state-handling") : a reload / resume onto an
  // authorization the Certificate Authority (CA) already marked
  // invalid previously mounted as a fresh "idle" panel - numbered
  // sequence, live buttons - inviting the customer to re-deploy and
  // re-trigger a dead authorization whose rejections then read like
  // fixable mistakes. RFC 8555 §7.1.6 defines no transition out of
  // invalid, so the panel mounts straight into the terminal state.
  const [checkState, setCheckState] = useState(
    entry.status === "valid"
      ? "valid"
      : entry.status === "invalid"
        ? "invalid"
        : "idle"
  );
  // Seeded for the mount-time invalid case so the terminal Alert
  // renders immediately : entry.errorDetail carries the CA's own
  // diagnostic when the /challenges view had one (the same
  // error?.detail join runCheck builds live). The no-detail fallback
  // ADDS information rather than restating the Alert title
  // (2026-08-14 go-public review : the former "VALIDATION FAILED" body under
  // the "DOMAIN VALIDATION FAILED PERMANENTLY" title read as the
  // same statement twice and told the customer nothing).
  const [checkError, setCheckError] = useState(
    entry.status === "invalid"
      ? entry.errorDetail || NO_CA_REASON_FALLBACK
      : null
  );
  // Which press produced the error on screen : true when checkError
  // was set by runTrigger. A failed Request Validation is a failed
  // REQUEST - nothing was checked - so its Alert carries its own title
  // and leaves out the check advice, which describes a press the
  // customer never made (operator ruling 2026-10-02). Cleared wherever
  // checkError is cleared.
  const [checkErrorFromTrigger, setCheckErrorFromTrigger] = useState(false);
  // 2026-08-13 go-public cleanup (audit : "misleading-failure-state") :
  // the server's trigger cooldown is a NORMAL state, not a
  // failure - it means a validation request is already in flight. It
  // used to land in checkError and render under a red "VALIDATION
  // FAILED" telling the customer to go and fix a challenge that is
  // fine. It now renders as its own info notice (see the Alert below).
  // { code, message } rather than a bare string : the two cooldowns
  // need different titles and different advice, and a notice that
  // cannot say which one it is ends up telling a customer who just
  // pressed Check Validation Status to go and check the status.
  const [cooldownNotice, setCooldownNotice] = useState(null);
  // Trigger ("Request Validation") is a separate action from the poll.
  const [triggering, setTriggering] = useState(false);
  // Fruitless-check counter (2026-08-15 design overhaul, from the
  // operator's own first live test : ten minutes of pressing Check
  // against a validation attempt that had already run and failed
  // quietly). Counts checks that came back still-pending since the
  // LAST trigger ; at two or more the pending notice escalates to
  // the amber re-request hint. Reset on every successful trigger,
  // because a fresh attempt restarts the story.
  const [pendingChecks, setPendingChecks] = useState(0);
  // Set once the customer has asked Sectigo® to validate at least once,
  // so the UI can steer them to "Check Validation Status" rather than
  // re-triggering (which would reset Sectigo®'s in-flight attempt).
  // 2026-07-04 Step 3 presentation redesign : this flag now also drives
  // the emphasis swap between the Request Validation and Check
  // Validation Status buttons (see requestVariant / checkVariant
  // below). It is PER-PANEL, not per-method, by design : triggering
  // either method counts as validation-requested for the SHARED
  // authorization, so switching method after a trigger keeps the
  // swapped emphasis.
  const [triggerRequested, setTriggerRequested] = useState(false);
  // 2026-07-04 Step 3 presentation redesign (operator direction : one
  // method at a time, numbered sequence, no polling) : which method's
  // numbered sequence is on screen ("dns" | "http"). DNS-01 is the
  // default because it exists for every domain, while HTTP-01 is
  // absent for wildcards (see the wildcard note in the header). The
  // http fallback covers the defensive case of a dns-less entry so the
  // panel never initialises onto a method it cannot render.
  const [method, setMethod] = useState(entry.dns ? "dns" : "http");

  /**
   * Milliseconds until Request Validation may be pressed again, per
   * challenge URL, counted down live.
   *
   * OPERATOR RULING, 2026-08-22, from his own live run : "when there is
   * a cooldown the buttons have a timer on them to let the customer
   * know which is more obvious and would perhaps even prevent them from
   * clicking the button at all". Until now the wait was discoverable
   * only by pressing the button and being refused - a cooldown that
   * teaches you it exists by punishing you for not knowing.
   *
   * SEEDED FROM THE SERVER, not from the moment of the press. The
   * challenge details carry triggerCooldownRemainingMs, so the timer is
   * already correct when Step 3 first paints, survives a reload, and is
   * right on a second machine resuming the same issuance request - none
   * of which a locally-started timer manages. A refusal re-seeds it from
   * that response's retryAfterMs, so the two can never drift apart.
   *
   * The resume case only works because the server reports the greater of
   * the per-session timestamp and the SHARED store claim : a resumed
   * session is a brand-new session with an empty timestamp map, so
   * without the shared peek this painted "Request Validation" and the
   * press was refused - the very thing the countdown exists to prevent.
   *
   * Keyed by challenge URL because the cooldown is : the server tracks
   * lastTriggerAtByChallenge, so validating one domain must never put
   * another domain's button on a timer. That is precisely the case
   * useMultiCooldown exists for, and tracking-01's own example for the
   * hook is "per-domain validation updates".
   */
  const triggerCooldown = useMultiCooldown(TRIGGER_COOLDOWN_MS);
  /**
   * The same treatment for Check Validation Status, whose wait is 30
   * seconds rather than five minutes. He judged this "isn't the biggest
   * problem" next to Request Validation, and that is a ranking of two
   * faults rather than an acquittal of one : both buttons refuse a
   * press with a 429 the customer had no way to anticipate, so both
   * carry a timer. The shorter period renders as "12s" rather than
   * "0:12" - see formatCooldownTime for why the shape follows the
   * period.
   */
  const checkCooldown = useMultiCooldown(CHECK_COOLDOWN_MS);

  // Plant both timers from what the server reported for this
  // authorization, so Step 3 paints a correct countdown before the
  // customer has pressed anything. Runs on every entry change because a
  // refreshed challenge list carries fresh figures.
  const { seed: seedTriggerCooldown } = triggerCooldown;
  useEffect(() => {
    seedTriggerCooldown({
      ...(entry.dns?.challengeUrl
        ? { [entry.dns.challengeUrl]: entry.dns.triggerCooldownRemainingMs }
        : {}),
      ...(entry.http?.challengeUrl
        ? { [entry.http.challengeUrl]: entry.http.triggerCooldownRemainingMs }
        : {}),
    });
  }, [
    seedTriggerCooldown,
    entry.dns?.challengeUrl,
    entry.dns?.triggerCooldownRemainingMs,
    entry.http?.challengeUrl,
    entry.http?.triggerCooldownRemainingMs,
  ]);

  // AND THE CHECK WINDOW TOO (round 3 of PR #106). It shipped without
  // one, which left the shortest of the three cooldowns as the only
  // button a customer could still meet by surprise : check, reload
  // Step 3 inside thirty seconds, press, refused.
  const { seed: seedCheckCooldown } = checkCooldown;
  useEffect(() => {
    seedCheckCooldown({
      ...(entry.dns?.challengeUrl
        ? { [entry.dns.challengeUrl]: entry.dns.checkCooldownRemainingMs }
        : {}),
      ...(entry.http?.challengeUrl
        ? { [entry.http.challengeUrl]: entry.http.checkCooldownRemainingMs }
        : {}),
    });
  }, [
    seedCheckCooldown,
    entry.dns?.challengeUrl,
    entry.dns?.checkCooldownRemainingMs,
    entry.http?.challengeUrl,
    entry.http?.checkCooldownRemainingMs,
  ]);
  // 2026-05-31 seventh-round code review nit-4, preserved through the
  // 2026-08-13 hook migration : copyStatus is a SINGLE-ENTRY object
  // keyed by the most-recently-copied field, so only one Copy button
  // gives feedback at a time (copy the DNS value then the HTTP value
  // and the DNS indicator disappears immediately ; switching method
  // mid-feedback must not resurrect a stale indicator). The shared
  // useCopyFeedback hook keeps exactly that replace-not-merge shape.
  // 2026-08-13 go-public cleanup (audit : "copy-feedback-drift") : the
  // hand-rolled state + timer + unmount cleanup is replaced by the
  // hook, which adds two things this copy never had : the generation
  // guard for the async clipboard gap (a late resolution can no
  // longer light feedback after a reset or beside the wrong value)
  // and a rendered FAILED state (the old render only tested
  // === "copied", so a failed clipboard write on a challenge value
  // produced no feedback at all ; the hook stores the success flag,
  // false = failed, and the buttons below render it).
  const { copyStatus, handleCopy } = useCopyFeedback();

  // Keyboard focus + announcement for the terminal outcomes
  // (2026-08-14 go-public accessibility review) : a successful
  // "Check Validation Status" unmounts the focused button and replaces the
  // whole sequence with the visual Validated badge - a screen-reader
  // customer heard NOTHING and their focus silently dropped to
  // <body>. When checkState transitions out of "checking" into a
  // terminal state, focus moves to this panel's domain heading (the
  // announcement itself comes from the sr-only live region in the
  // render below, plus the live error Alert for failures). The
  // prev-state ref restricts the move to LIVE transitions : a panel
  // that MOUNTS valid / invalid (reload, resume) never steals focus.
  const panelHeadingRef = useRef(null);
  const prevCheckStateRef = useRef(checkState);
  useEffect(() => {
    const prev = prevCheckStateRef.current;
    prevCheckStateRef.current = checkState;
    if (
      (checkState === "valid" || checkState === "invalid") &&
      prev === "checking" &&
      panelHeadingRef.current
    ) {
      panelHeadingRef.current.focus();
    }
  }, [checkState]);

  // Track mounted state so async work (the Sectigo® trigger and
  // status-poll round-trips in runTrigger / runCheck) cannot setState
  // after the customer has navigated away or reset the page.
  // (2026-07-04 : "Check Now" reference retired - that fused button
  // was split into "Request Validation" + "Check Validation Status".)
  // The copy-status timer that review I2 (2026-05-31) taught this
  // effect to cancel lives inside useCopyFeedback now, which owns its
  // own unmount cleanup.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDownloadHttpFile = () => {
    if (!http) return;
    // HTTP-01 challenge file is served at
    //   http://<domain>/.well-known/acme-challenge/<token>
    // with no extension. The download filename MUST be the EXACT token
    // so the customer can drop the file straight into that path.
    //
    // 2026-07-03 PR #76 sixth-pass review (Finding 4b) DECLINED with
    // evidence : the review suggested routing http.token through the
    // shared sanitizeFilename "for consistency". We deliberately do NOT,
    // because it would BREAK validation :
    //   - RFC 8555 §8.1 constrains the token to the base64url alphabet
    //     [A-Za-z0-9_-] ; it can never contain "/", "..", or control
    //     characters, so there is no path-traversal to sanitise. (And a
    //     browser's download attribute already strips path separators
    //     from a suggested filename, so even a hypothetical bad token
    //     could not traverse the filesystem.)
    //   - sanitizeFilename is NOT identity on that alphabet : it strips
    //     leading/trailing "-"/"_", collapses "--"/"__" runs, and
    //     truncates at 50 chars. A token like "-a__b-" would download as
    //     "a_b", NOT matching the /.well-known/acme-challenge/<token>
    //     path Sectigo® checks - so HTTP-01 would silently fail.
    // The token MUST travel byte-for-byte ; using it raw is correct.
    downloadFile(http.fileContents, http.token, "text/plain");
  };

  /**
   * "Request Validation" (numbered section 2) - ask Sectigo® to validate
   * the SELECTED method's challenge by POSTing {} to its challenge
   * URL. Press ONCE after deploying the record or file ; the server
   * rate-limits rapid re-triggers (a 429 here means "wait and check",
   * not a failure). Re-triggering too soon is exactly what wedges a
   * challenge, so after a successful trigger the emphasis swap (see
   * requestVariant / checkVariant) steers the customer to the
   * read-only status check.
   */
  const runTrigger = async (chall) => {
    if (!chall) return;
    setTriggering(true);
    setCheckError(null);
    setCheckErrorFromTrigger(false);
    setCooldownNotice(null);
    // Bounded trigger (2026-08-14 go-public review, the wedge family's final
    // members) : `triggering` disables BOTH Step 3 buttons via
    // `busy`, so an unbounded hang froze this panel's controls
    // forever. NOTE : an aborted trigger may still have REACHED the
    // server, whose cooldown arm is write-ahead. The catch resyncs
    // both windows from the server for exactly that reason, so the
    // retry meets a countdown rather than a refusal.
    try {
      await withTimeoutSignal(SHORT_CALL_TIMEOUT_MS, (signal) =>
        apiTriggerValidation(sessionId, chall.challengeUrl, { signal })
      );
      if (!mountedRef.current) return;
      setTriggerRequested(true);
      setPendingChecks(0);
      // The countdown starts HERE on the success path. It is NOT the
      // only place it can start : the catch resyncs from the server,
      // because the server's arm is write-ahead and an ambiguous
      // failure leaves the window held (round 4 of PR #106). The whole
      // point is that a customer never has to be refused in order to
      // learn there is a wait, and a failed press is where that was
      // still happening.
      triggerCooldown.startItemCooldown(chall.challengeUrl);
      // Validation is requested but not decided ; the customer polls with
      // "Check Validation Status" to read the result.
      setCheckState("pending");
    } catch (e) {
      if (!mountedRef.current) return;
      if (e && e.name === "AbortError") {
        // Our own bounded-controller timeout : branded line instead of
        // the raw lowercase browser prose. The aborted POST may well
        // have REACHED the server (the write-ahead cooldown arm - see
        // the bounded-trigger note above), so the message steers at
        // Check Validation Status and triggerRequested flips the emphasis
        // swap onto that button (2026-08-14 go-public review : the old
        // "PLEASE TRY AGAIN" aimed the customer at the one button the
        // trigger cooldown would now refuse, with the check button
        // still de-emphasised).
        setCheckError(
          "The validation request timed out. Please check the validation status to see whether your request was received."
        );
        setCheckErrorFromTrigger(true);
        setTriggerRequested(true);
        // THE RESYNC BELONGS HERE TOO, and this branch is the one that
        // needs it most : a timeout is the single most likely way for
        // the server to be holding the window while this browser has
        // nothing to show for the press. The branch returns early, so
        // a call at the end of the catch would never reach it.
        //
        // NOT AWAITED, and that single word carries two fixes (rounds
        // 5 and 6 of PR #106). Awaiting it here kept `triggering` set
        // until the read settled, freezing every control on the panel
        // for the resync's own fifteen seconds ON TOP of the fifteen
        // the trigger had just spent - the customer read "PLEASE CHECK
        // THE VALIDATION STATUS" beside a disabled Check Validation
        // Status. Releasing the flag before the await fixed that and
        // introduced a worse one : the `finally` then fired fifteen
        // seconds later, from a closure whose press was long over, and
        // cleared whatever `triggering` held by then - including the
        // flag a SECOND press had set, dropping the spinner off a live
        // request and re-enabling a button whose disabled state was
        // the only re-entrancy guard this function has.
        //
        // Firing and forgetting removes the class rather than guarding
        // it. The catch returns immediately, so the `finally` runs
        // before any second press can exist and is once again the
        // backstop it reads as. The seed lands whenever the read
        // answers, and cannot land wrong : seed keeps whichever
        // deadline expires LATER and ignores non-positive figures, so
        // a stale answer arriving after a fresh press cannot shorten
        // or clear the window that press armed.
        //
        // The press that can land between the error and the seeded
        // countdown is the same window runCheck has always lived with,
        // and the 429 info notice with its retryAfterMs re-seed is its
        // backstop.
        void resyncCooldownsFromServer();
        return;
      }
      // The trigger cooldown is informational, never an
      // error : it means the previous press already asked the
      // Certificate Authority (CA) to validate, so the right next
      // action is "Check Validation Status" - which the emphasis swap now
      // steers to, exactly as if this press had triggered.
      // Recognised by the machine-readable envelope code, NOT the
      // English message prefix (2026-08-14 go-public review : a
      // prefix match means any wording pass on the server line
      // silently returns the cooldown to the red failure treatment,
      // and no test on this side could catch it). The status check
      // stays as belt-and-braces : the
      // per-session check-cap also answers 429 but carries no code,
      // so it keeps the error treatment it needs.
      if (
        e instanceof ApiError &&
        e.status === 429 &&
        e.body &&
        e.body.code === TRIGGER_COOLDOWN_CODE
      ) {
        setCooldownNotice({ code: TRIGGER_COOLDOWN_CODE, message: e.message });
        setTriggerRequested(true);
        // Re-seed the countdown from the refusal's own figure so the
        // button and the notice can never disagree about the wait.
        if (typeof e.body?.retryAfterMs === "number") {
          triggerCooldown.seed({
            [chall.challengeUrl]: e.body.retryAfterMs,
          });
        }
        return;
      }
      setCheckError(
        e instanceof ApiError
          ? e.message
          : "The validation request failed. " + (e.message || "Unknown error.")
      );
      setCheckErrorFromTrigger(true);
      // The press failed AMBIGUOUSLY : the server may well be holding
      // the five-minute window even though this call did not come back
      // (see resyncCooldownsFromServer). Ask, rather than leave the
      // button inviting a press that will be refused. Fire and forget,
      // for both reasons set out in the timeout branch above.
      void resyncCooldownsFromServer();
    } finally {
      if (mountedRef.current) setTriggering(false);
    }
  };

  /**
   * After a press FAILS, ask the server where both windows actually
   * stand and plant what it says.
   *
   * ROUND 4 OF PR #106. The client armed its countdowns only on the
   * success path, while the server arms WRITE-AHEAD : both
   * triggerChallengeValidation and checkChallenge record their
   * timestamp before the Certificate Authority call and their catches
   * revert it ONLY when the Certificate Authority provably acted on
   * nothing : the transport classes that prove the request never left
   * the socket, or a failed nonce request. Every ambiguous
   * outcome - our own timeout, a lost response, an upstream 5xx after
   * receipt - therefore left the server holding the window while the
   * button painted as ready. The customer then does the one thing a
   * failure message invites, presses again, and is refused. That is
   * the fault this whole change exists to remove, surviving on the
   * longest window and at the moment a press is most likely.
   *
   * ASKED, NOT INFERRED. The obvious patch is to arm locally whenever
   * the press aborts, but that is a guess in both directions : a
   * request that never reached the server leaves nothing armed, and a
   * CA 5xx after receipt leaves both windows armed on a press we would
   * not have counted. getChallengeDetails already reports both figures
   * authoritatively, so one read settles every class instead of
   * classifying them from the browser, where the information does not
   * exist. Worth the round trip here because the trigger window is
   * five minutes : withholding it wrongly, or failing to show it,
   * both cost real time.
   *
   * BEST EFFORT BY DESIGN. If this read fails too - the same outage
   * that failed the press, most likely - it is silent. It corrects a
   * countdown ; it must never replace the error the customer needs to
   * read, and the 429 re-seed remains the backstop it always was.
   */
  const resyncCooldownsFromServer = async () => {
    try {
      const entries = await withTimeoutSignal(SHORT_CALL_TIMEOUT_MS, (signal) =>
        apiGetChallenges(sessionId, { signal })
      );
      if (!mountedRef.current || !Array.isArray(entries)) return;
      const mine = entries.find((e) => e && e.authUrl === authUrl);
      if (!mine) return;
      const triggers = {};
      const checks = {};
      for (const method of [mine.dns, mine.http]) {
        if (!method || !method.challengeUrl) continue;
        triggers[method.challengeUrl] = method.triggerCooldownRemainingMs;
        checks[method.challengeUrl] = method.checkCooldownRemainingMs;
      }
      triggerCooldown.seed(triggers);
      checkCooldown.seed(checks);
      // A HELD SERVER WINDOW IS PROOF THE REQUEST REACHED THE SERVER
      // (2026-08-23 review of PR #107). The arm is write-ahead, so a
      // positive remaining time can only exist because the server took
      // a trigger for this challenge - which means Check Validation
      // Status has something to read, whatever the mount-time
      // challenge status still says.
      //
      // Without this the generic failure branch left BOTH controls
      // dead : a CA call that fails after submit answers 500, the
      // resync seeds ~4:54 onto Request Validation and disables it,
      // and Check stayed disabled on a stale "pending". The customer
      // read "VALIDATION REQUEST FAILED" with nothing to press for
      // five minutes - having lost the one genuinely useful action in
      // that state, which is asking the CA whether it got the request.
      //
      // Set here rather than in each catch so every failure branch is
      // covered by the same evidence, including the two that already
      // set it for weaker reasons.
      if (
        Object.values(triggers).some((ms) => typeof ms === "number" && ms > 0)
      ) {
        setTriggerRequested(true);
      }
    } catch {
      // Deliberately swallowed - see BEST EFFORT above.
    }
  };

  /**
   * "Check Validation Status" (numbered section 2) - the READ-ONLY
   * status poll. Reads the current authorization status WITHOUT
   * re-triggering anything, so it can never disturb Sectigo®'s
   * in-flight validation.
   * No looping - the customer presses again if it is not yet valid.
   */
  const runCheck = async (chall) => {
    if (!chall) return;
    // Captured before the transition so the cooldown branch below can
    // put it back - see the restore there.
    const stateBeforeCheck = checkState;
    setCheckState("checking");
    setCheckError(null);
    setCheckErrorFromTrigger(false);
    // A check supersedes the cooldown notice : its outcome (pending /
    // valid / invalid) is fresher information than "wait a moment".
    setCooldownNotice(null);
    // Bounded poll (2026-08-14 go-public review) : `checking` disables both
    // buttons via `busy`, same wedge as the trigger above.
    try {
      const auth = await withTimeoutSignal(SHORT_CALL_TIMEOUT_MS, (signal) =>
        apiCheckChallenge(sessionId, chall.challengeUrl, { signal })
      );
      // Bail if the component unmounted while the apiCheckChallenge
      // round-trip was in flight - the same mountedRef unmount-guard
      // pattern runTrigger applies above. (2026-07-04 : this comment
      // used to cite "the DoH guard above", a DNS-over-HTTPS pre-check
      // that was removed in the server-side pivot ; mountedRef is the
      // guard that survives.) onValidated is the parent's callback,
      // which is itself unsafe to invoke after the parent has
      // unmounted.
      if (!mountedRef.current) return;
      // 2026-07-03 PR #76 seventh-pass review #3 : apiClient `call`
      // resolves an off-contract 200-with-empty-body to null. Reading
      // auth.status on a null would throw a raw lowercase TypeError that
      // the catch below interpolates verbatim into the customer Alert.
      // Throw a house-style sentence first - the catch wraps it as
      // "The status check failed. <reason>", so a bare reason avoids
      // doubling the prefix.
      // The wait starts on the ATTEMPT, not on a particular answer -
      // the server's window opened when it took the request, so a
      // check that read "invalid" has spent it exactly like one that
      // read "pending". tracking-01's COOLDOWN-MANAGEMENT rule 5 draws
      // this same line : a cooldown protecting a BUDGET starts
      // regardless of outcome, unlike one preventing a duplicate.
      checkCooldown.startItemCooldown(chall.challengeUrl);
      if (!auth) {
        throw new Error("The server returned an empty challenge status.");
      }
      if (auth.status === "valid") {
        setCheckState("valid");
        onValidated && onValidated(authUrl);
        return;
      }
      if (auth.status === "invalid") {
        setCheckState("invalid");
        // Live terminal transition : tell the parent (see the
        // onInvalid prop JSDoc). Same call shape as onValidated.
        onInvalid && onInvalid(authUrl);
        // The detail string from Sectigo® arrives as their wording ;
        // we surface it verbatim because rewriting it would destroy
        // the customer's diagnostic information. The fallback we own
        // is a plain sentence (2026-08-27).
        const detail =
          joinChallengeErrorDetails(auth.challenges) || NO_CA_REASON_FALLBACK;
        setCheckError(detail);
        return;
      }
      // pending or processing - Sectigo® has not decided yet. The customer
      // waits and checks again (or re-triggers if it stays stuck).
      setCheckState("pending");
      setPendingChecks((n) => n + 1);
    } catch (e) {
      if (!mountedRef.current) return;
      setCheckState("idle");
      // The poll cooldown is NOT a failure : nothing went wrong and
      // the request is untouched, the customer simply asked again
      // sooner than the window allows. Same info-notice treatment the
      // trigger cooldown gets above, keyed on the shared code rather
      // than the message text so a wording pass on the server line
      // cannot silently return it to the red Alert.
      if (
        e instanceof ApiError &&
        e.status === 429 &&
        e.body &&
        e.body.code === CHECK_COOLDOWN_CODE
      ) {
        // RESTORE the status this press did not change. The catch
        // above set "idle", which discards whatever the last real
        // check read - so a customer who checked once (panel reads
        // pending, its Alert on screen) and pressed again inside the
        // window lost that result on a press that by definition
        // learned nothing.
        setCheckState(stateBeforeCheck);
        setCooldownNotice({ code: CHECK_COOLDOWN_CODE, message: e.message });
        // Same re-seed the trigger branch does : the server's figure is
        // authoritative, so the button and the notice cannot disagree.
        if (typeof e.body?.retryAfterMs === "number") {
          checkCooldown.seed({ [chall.challengeUrl]: e.body.retryAfterMs });
        }
        return;
      }
      setCheckError(
        e && e.name === "AbortError"
          ? "The validation check timed out. Please try again."
          : e instanceof ApiError
            ? e.message
            : "The status check failed. " + (e.message || "Unknown error.")
      );
      // Same ambiguity as the trigger, against the thirty-second
      // window : checkChallenge arms write-ahead too.
      await resyncCooldownsFromServer();
    }
  };

  const isChecking = checkState === "checking";
  const isValid = checkState === "valid";
  // Terminal under RFC 8555 §7.1.6 : no transition out of invalid
  // exists, so the whole interactive sequence is withdrawn (see the
  // render gate below) and only the terminal-guidance Alert remains
  // (2026-08-14 go-public review : buttons left live here would let
  // every press burn per-session request budget against an
  // authorization that can never validate).
  const terminalInvalid = checkState === "invalid";
  // Any in-flight request disables the buttons ; the trigger and the poll
  // are separate actions but only one runs at a time per panel.
  const busy = isChecking || triggering;
  // The challenge whose numbered sequence renders. Either method's
  // challengeUrl locates the SHARED authorization for both the trigger
  // and the read-only poll (both challenges belong to the same authz),
  // so `selected` safely drives both of section 2's buttons as well
  // as the section 1 data. DNS-01 is always present ; HTTP-01 only for
  // non-wildcard domains.
  const selected = method === "http" ? http : dns;
  // The countdown for whichever method is on screen. Keyed by challenge
  // URL because the server's cooldown is : switching between the DNS and
  // HTTP tabs shows each one's own remaining wait.
  // Both waits for the SELECTED method only - the other method's
  // challenge has its own independent cooldown and its own button is
  // not on screen.
  const selectedUrl = selected ? selected.challengeUrl : null;
  const triggerWaiting = triggerCooldown.isItemInCooldown(selectedUrl);
  const checkWaiting = checkCooldown.isItemInCooldown(selectedUrl);

  /**
   * Has validation been requested for this domain yet?
   *
   * OPERATOR DIRECTIVE, 2026-08-22 : "why is that button to check the
   * status enabled when you already know that the first button has
   * never been pushed?" There was no answer. Check Validation Status
   * fetches the RESULT of a request, so with no request it can only
   * report the same nothing however many times it is pressed - and
   * every condition that had ever gated it tested whether a press
   * would be REFUSED (a call in flight, no method chosen, the
   * cooldown), never whether a press had any BASIS.
   *
   * THE CERTIFICATE AUTHORITY'S OWN CHALLENGE STATUS IS THE SOURCE,
   * because it is the only one that answers in every case. Our
   * per-session record (lastTriggerAtByChallenge) is correct while the
   * session lives and unreachable exactly when it matters most : a
   * session is encrypted with a key derived from the customer's own
   * token, so someone returning on another machine with only their
   * Configuration File cannot open the old one at all - by design,
   * there is no master key. That resume builds a NEW session, and the
   * file carries the account key, the order URL and the private key,
   * but nothing about validation. The challenge status survives all of
   * it, because it is the CA's record and startIssuance re-fetches
   * every authorization from the order on load.
   *
   * ALREADY ON THE PAGE : getChallengeDetails has always sent `status`
   * on both challenge branches, and this panel has always discarded it
   * in favour of the AUTHORIZATION status. No new field, no new
   * request, no new storage.
   *
   * AUTHORIZATION-WIDE, not per method : a check calls
   * fetchAuthorization, so it reads the domain rather than the one
   * challenge. Requesting via DNS makes a check meaningful while the
   * HTTP tab is showing.
   *
   * FAILS OPEN. An absent status counts as requested, so an
   * unrecognised CA response can only ever leave the button as it is
   * today. Locking someone out of the one control that tells them
   * where their domain stands is a worse failure than letting them
   * press a button that answers nothing.
   */
  const challengeRequested = (c) => !!c && c.status !== "pending";
  /**
   * A held server window is proof AT MOUNT too (2026-08-23 review of
   * PR #107 round 2). The arm is write-ahead, so a positive remaining
   * figure can only exist because the server took a trigger for this
   * challenge - the same evidence resyncCooldownsFromServer already
   * trusts, which was being believed live and forgotten on reload.
   *
   * The gap it closes is an ambiguous trigger failure FOLLOWED BY a
   * reload, which is exactly what a customer does after reading
   * VALIDATION REQUEST FAILED. The throw skips the status splice, so
   * the stored challenge stays "pending" ; the arm and the shared
   * claim both hold, because a post-submit failure is not a pre-submit
   * network error. On remount triggerRequested is false and the status
   * is stale, so Check was disabled - while that same held window
   * disabled Request Validation. Every control dead, and the only one
   * that revived steered into a re-trigger.
   *
   * It also covers a Configuration File resume on a second machine
   * inside the window, where the shared-claim peek is what
   * getChallengeDetails reports.
   */
  const heldByServer = (c) => !!c && c.triggerCooldownRemainingMs > 0;
  const validationRequested =
    // The local flag first : it flips on the press itself, so the
    // button unlocks immediately rather than waiting for a refetch
    // Step 3 does not perform. It is set on a successful request, on
    // our own timeout (which may still have reached the CA) and on the
    // cooldown refusal (which is proof a request already exists).
    triggerRequested ||
    challengeRequested(dns) ||
    challengeRequested(http) ||
    heldByServer(dns) ||
    heldByServer(http);
  // ONE construction of the displayed file address (2026-08-14
  // go-public review) : if the <dd> and the Copy File Address handler
  // each built "http://" + domain + filePath independently, two
  // constructions of one transcribable value drift the first time
  // the shape changes (port, scheme), and the copy control exists
  // precisely so the customer never retypes what they see.
  const httpFileAddress = http ? "http://" + domain + http.filePath : null;
  // The method selector renders only when there is a genuine choice :
  // both challenges present and not a wildcard. Wildcard domains are
  // DNS-01-only (the header note explains why), so they get the DNS
  // sequence directly with no selector - same `http && !wildcard`
  // guard the pre-redesign HTTP-01 column used.
  const showSelector = Boolean(dns && http && !wildcard);
  // 2026-07-04 Step 3 presentation redesign : state-aware emphasis,
  // still fully manual. Before the first successful trigger the
  // primary (filled) action is "Request Validation" and the check is
  // outline ; after a successful trigger the emphasis SWAPS so the
  // customer's eye lands on the read-only check instead of a
  // challenge-wedging re-trigger. The EMPHASIS is all this decides :
  // whether the button is pressable is decided by triggerWaiting on
  // the button itself, which disables it for the whole five-minute
  // window while the label counts the wait down (2026-08-22). This
  // comment used to say the opposite - that the button stays enabled
  // and the server's 429 is what stops a second press - and that was
  // the contract until this change removed it. Meeting the 429 is now
  // the backstop for a press slipping through the gap between a
  // failed trigger and the seeded countdown, not the ordinary path.
  const requestVariant = triggerRequested ? "outline" : "primary";
  const checkVariant = triggerRequested ? "primary" : "outline";

  return (
    /*
      2026-08-15 design overhaul (operator-approved mockup) : the panel
      is a bordered card with a header BAND (mono domain + status
      pill) and a padded body, replacing the flat tinted inset - on a
      multi-domain order each domain reads as its own clearly-bounded
      unit, and the pill answers "where is this domain up to?" without
      reading any prose. The 2026-08-13 flattening rationale this
      supersedes solved box-on-identical-box nesting ; the new frame
      is a genuine two-tone card, not a box drawn on its own colour.
    */
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-6 py-4 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="min-w-0">
          {/*
            tabIndex -1 : programmatically focusable for the terminal-
            outcome focus move (see the panelHeadingRef effect above),
            never in the Tab order. Visible focus ring (2026-08-14
            go-public review, same posture as the StepPanel heading) : a
            sighted keyboard-only customer must SEE where focus landed
            when the button they pressed unmounts under them.
          */}
          <h3
            ref={panelHeadingRef}
            tabIndex={-1}
            className="font-mono font-semibold text-heading break-all focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm"
          >
            {challengeDomainLabel(entry)}
          </h3>
          {/*
            ONLY WHILE THERE IS STILL SOMETHING TO DO (operator, from
            his live run : "when a domain is validated it shouldn't
            have this sentence"). The note exists to explain why a
            wildcard offers no method selector. Once the domain is
            validated - or terminally invalid, where the whole
            sequence is withdrawn - there is no method left to choose,
            so it is answering a question nobody is asking and sits
            directly above a green Validated pill saying the work is
            done. Same condition the sequence render gate uses, not a
            second parallel one.
          */}
          {wildcard && !isValid && !terminalInvalid && (
            <p className="text-body mt-0.5">
              Wildcard domain - the Automatic Certificate Management Environment
              (ACME) standard permits DNS-01 validation only.
            </p>
          )}
          {/*
            Persistent sr-only live region (2026-08-14 go-public
            accessibility review) : announces the per-domain outcome
            that the purely visual status pill otherwise conveys
            silently. Persistent (always rendered) rather than
            conditionally inserted, because live regions announce
            CONTENT CHANGES reliably only when the region itself
            already exists in the accessibility tree. Polite : an
            outcome is a progress update, not an interrupt - failures
            additionally get the assertive live error Alert below.
          */}
          <div role="status" aria-live="polite" className="sr-only">
            {isValid
              ? `${challengeDomainLabel(entry)} Validated`
              : terminalInvalid
                ? `${challengeDomainLabel(entry)} Validation Failed Permanently`
                : null}
          </div>
        </div>
        {/* The status pill : amber while work remains, green when the
            authorization reads valid, red when terminally invalid.
            Real text (never colour alone), so the state survives any
            colour-vision profile. */}
        {isValid ? (
          <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 whitespace-nowrap">
            <CheckCircleIcon className="w-4 h-4" aria-hidden="true" />
            Validated
          </span>
        ) : terminalInvalid ? (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 whitespace-nowrap">
            Validation Failed
          </span>
        ) : (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 whitespace-nowrap">
            Awaiting Validation
          </span>
        )}
      </div>

      {/*
        THE BODY ALWAYS RENDERS, so the card keeps ONE shape from the
        moment Step 3 loads to the moment the customer leaves it.

        OPERATOR DIRECTIVE, 2026-08-22, from his live run : "as each
        one has become validated - why is the format changing? Why
        isn't it kept the same as it already was and simply removing
        the buttons and putting some centered text there in bold... I
        am trying to keep this as consistent as possible and by you
        changing the format and having additional messaging boxes in
        different situations it becomes complicated."

        Before this, a domain had THREE presentations depending on
        when it validated : a full card while pending, a collapsed
        header band with no body once validated during the session,
        and NO CARD AT ALL when it was already valid as Step 3 loaded
        (Step3Verify filtered those out and named them in a separate
        green Alert instead). Three shapes for one thing, and the
        shape changed under the customer as they worked.

        Now the card never changes shape : the header band and the
        status pill are constant, and only the body's CONTENT swaps
        between the numbered sequence, the terminal guidance, and the
        validated line. The empty-box problem the old gate was
        avoiding is solved by giving the validated state real content
        rather than by removing the container.
      */}
      <div className="p-6">
        {/*
            The validated state's whole interface : the sequence, both
            action buttons and the method selector are gone, because
            there is nothing left to do on this domain. Centered and
            bold to his specification.

            THE STEP'S OWN WORDS (operator-chosen, 2026-08-22). Step 3
            is titled "Verify Domain Ownership", so its completion line
            reports that result in the same language the step announces
            itself with. His first draft read "Record Validated and
            Finalized", and all three nouns misfired : "Record" is
            wrong for a customer who uploaded an HTTP file and has no
            record, "Validated" repeats the pill directly above it in
            the same card, and "Finalized" is ACME's word for what
            Step 4 does when the SSL Certificate is actually signed -
            reading it here invites a customer to think issuance is
            already done.

            ONE sentence covering both situations deliberately - it is
            the same string whether other domains are still outstanding
            or not, because a string that changes with the rest of the
            page is another format change of the kind this directive
            exists to stop.
          */}
        {isValid && (
          <p className="text-center font-semibold text-heading">
            Domain Ownership Verified - Continue or Complete Outstanding
            Validations
          </p>
        )}
        {/* Once the authorization reads valid there is nothing left for
            the customer to do on this domain, so the validated line
            above replaces the whole sequence (selector, challenge data,
            and both action buttons) while the card around it stays put.
            The terminal INVALID state withdraws the sequence too
            (2026-08-14 go-public review) : rendering deploy
            instructions and live trigger/check buttons for an
            authorization the CA can never validate again both misleads
            and burns the per-session request budget - the terminal
            Alert below is the whole remaining interface. */}
        {!isValid && !terminalInvalid && (
          <>
            {showSelector && (
              <p className="text-body mb-2">
                Choose how you want to prove you control this domain. Both
                methods work equally well - pick whichever you can do more
                easily.
              </p>
            )}
            {showSelector && (
              /* Segmented control - the site tab idiom at panel scale.
                 Switching is presentation-only : it swaps which
                 method's sequence renders and which challengeUrl the
                 buttons use ; it never resets triggerRequested (see
                 that state's comment). Short labels (2026-08-15
                 design overhaul) : the protocol identifiers DNS-01 /
                 HTTP-01 already appear in the guidance sentences, so
                 the toggle carries the customer words only.

                 THE UNSELECTED CHIP'S BASE IS text-body, NOT
                 text-gray-700 dark:text-gray-300 (2026-08-25). With a
                 dark:text-* on the element the collapsed
                 disabled:text-muted never fired in dark mode : both rules
                 score (0,2,0), and Tailwind emits the dark variant group
                 AFTER the pseudo-class group, so the base colour won on
                 source order and a locked chip kept its live tone. A role
                 name carries no dark: half, so (0,2,0) beats (0,1,0) and
                 the disabled rule reaches the element in both themes.

                 THE GENERAL PRECONDITION, before collapsing any other
                 dark:disabled: pair : it is only safe when the element
                 has no dark:text-* base of its own. Every other
                 disabled:text-muted on the site sits on a text-link base
                 with no dark: class, which is why those were correct. */
              <div
                role="group"
                aria-label="Validation Method"
                className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-900/50 p-1 gap-1 mb-6"
              >
                <button
                  type="button"
                  onClick={() => setMethod("dns")}
                  disabled={busy}
                  aria-pressed={method === "dns"}
                  className={`px-4 py-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    method === "dns"
                      ? "bg-blue-600 text-white"
                      : "text-body hover:bg-gray-200 dark:hover:bg-gray-700 disabled:text-muted disabled:hover:bg-transparent"
                  }`}
                >
                  DNS Record
                </button>
                <button
                  type="button"
                  onClick={() => setMethod("http")}
                  disabled={busy}
                  aria-pressed={method === "http"}
                  className={`px-4 py-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    method === "http"
                      ? "bg-blue-600 text-white"
                      : "text-body hover:bg-gray-200 dark:hover:bg-gray-700 disabled:text-muted disabled:hover:bg-transparent"
                  }`}
                >
                  HTTP File
                </button>
              </div>
            )}

            {/* Numbered section 1 : the selected method's challenge
                data. The guidance sentences are the operator-ruled
                2026-08-13 content (the no-file-extension trap for
                HTTP, the host-part-only convention for DNS),
                unchanged - only their placement moved beside the
                heading they explain. */}
            <div>
              <StepHeading
                number={1}
                description={
                  method === "http"
                    ? "Upload the file below so it is served at the exact address shown - the contents must match exactly, and the file must have no file extension (if your computer adds .txt when you save it, remove it before uploading)."
                    : "Create the Text (TXT) record below with your Domain Name System (DNS) provider - the value must match exactly, so please use the Copy buttons rather than retyping. Some providers ask only for the part in front of your domain - if yours adds the domain for you, enter _acme-challenge on its own."
                }
              >
                {method === "http" ? "File to Upload" : "DNS Record to Create"}
              </StepHeading>

              {/* The record card (2026-08-15 design overhaul) : the
                  transcribable values are the panel's visual hero -
                  bordered labelled rows with an attached Copy control
                  on every value the customer carries elsewhere. */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden mt-4">
                {method === "http" && http ? (
                  <>
                    {/* "File Address" (2026-08-13 content ruling) : the
                        rendered value is a full URL, not a filesystem
                        path. */}
                    <ChallengeValueRow
                      label="File Address"
                      value={httpFileAddress}
                      copy={
                        <ChallengeCopyButton
                          label="Copy File Address"
                          failedLabel="Copy Failed - Select the Address Manually"
                          value={httpFileAddress}
                          fieldKey="http-address"
                          copyStatus={copyStatus}
                          onCopy={handleCopy}
                          disabled={busy}
                        />
                      }
                    />
                    <ChallengeValueRow
                      label="File Contents"
                      value={http.fileContents}
                      copy={
                        <ChallengeCopyButton
                          label="Copy File Contents"
                          failedLabel="Copy Failed - Select the Text Manually"
                          value={http.fileContents}
                          fieldKey="http-content"
                          copyStatus={copyStatus}
                          onCopy={handleCopy}
                          disabled={busy}
                        />
                      }
                    />
                  </>
                ) : dns ? (
                  <>
                    <ChallengeValueRow
                      label="Record Name"
                      value={dns.recordName}
                      copy={
                        <ChallengeCopyButton
                          label="Copy Record Name"
                          failedLabel="Copy Failed - Select the Name Manually"
                          value={dns.recordName}
                          fieldKey="dns-name"
                          copyStatus={copyStatus}
                          onCopy={handleCopy}
                          disabled={busy}
                        />
                      }
                    />
                    <ChallengeValueRow
                      label="Record Type"
                      value={dns.recordType}
                    />
                    <ChallengeValueRow
                      label="Record Value"
                      value={dns.recordValue}
                      copy={
                        <ChallengeCopyButton
                          label="Copy Record Value"
                          failedLabel="Copy Failed - Select the Value Manually"
                          value={dns.recordValue}
                          fieldKey="dns-value"
                          copyStatus={copyStatus}
                          onCopy={handleCopy}
                          disabled={busy}
                        />
                      }
                    />
                  </>
                ) : null}
              </div>
              {method === "http" && http && (
                /* mt-4, this panel's WITHIN-block gap, matching the mt-4
                   from each StepHeading to its content. It was mt-3 -
                   the only 12px gap on the step. */
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleDownloadHttpFile}
                    disabled={busy}
                    className="inline-flex items-center font-semibold text-link px-3 py-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:text-muted disabled:cursor-not-allowed focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <ArrowDownTrayIcon
                      className="w-4 h-4 mr-1"
                      aria-hidden="true"
                    />
                    Download File
                  </button>
                </div>
              )}
            </div>

            {/* Numbered section 2 (2026-08-15 design overhaul) : the
                trigger and the read-only poll act as ONE ask-then-read
                pair, so they share a section, one guidance sentence,
                and an equal-width row - the former separate sections
                2 and 3 restated each other's mechanics. The emphasis
                swap (requestVariant / checkVariant) still steers the
                eye from the trigger to the check after a successful
                request. */}
            {/* mt-6, matching every other BLOCK gap in this panel : the
                selector's mb-6 above and the three notice Alerts' mt-6
                below (operator directive, 2026-08-23 : all line spacing
                between elements is the same). It was mt-8, so the one
                gap in the middle of the numbered sequence rendered 32px
                where both of its neighbours rendered 24px. */}
            <div className="mt-6">
              <StepHeading
                number={2}
                description={
                  method === "http"
                    ? "Request validation once the file is uploaded - the Certificate Authority (CA) checks it once each time you ask. Then check if it has validated to read the result."
                    : "Request validation once the Domain Name System (DNS) record is in place - the Certificate Authority (CA) checks it once each time you ask. Then check if it has validated to read the result."
                }
              >
                Validation Request and Result
              </StepHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {/* THE WAIT IS ON THE BUTTON (operator ruling,
                      2026-08-22) : "the buttons have a timer on them to
                      let the customer know ... and would perhaps even
                      prevent them from clicking the button at all". A
                      cooldown a customer can only discover by being
                      refused teaches them it exists by punishing them
                      for not knowing. Disabled while it runs, because a
                      press that can only be refused is not a choice. */}
                <ActionButton
                  onClick={() => runTrigger(selected)}
                  disabled={busy || !selected || triggerWaiting}
                  loading={triggering}
                  loadingText="Requesting Validation..."
                  icon={ArrowPathIcon}
                  variant={requestVariant}
                >
                  {triggerWaiting
                    ? cooldownLabel(
                        "Request Validation",
                        triggerCooldown.formatItemTimeRemaining(selectedUrl)
                      )
                    : "Request Validation"}
                </ActionButton>
                <ActionButton
                  onClick={() => runCheck(selected)}
                  disabled={
                    busy || !selected || checkWaiting || !validationRequested
                  }
                  loading={isChecking}
                  loadingText="Checking Status..."
                  icon={CheckCircleIcon}
                  variant={checkVariant}
                >
                  {checkWaiting
                    ? cooldownLabel(
                        "Check Validation Status",
                        checkCooldown.formatItemTimeRemaining(selectedUrl)
                      )
                    : "Check Validation Status"}
                </ActionButton>
              </div>
            </div>
          </>
        )}

        {/* STAGED pending guidance (2026-08-15 design overhaul, from
            the operator's own first live test : ten minutes of
            checking against a validation attempt that had already run
            and failed quietly). While the first checks are fresh the
            notice is the calm blue wait-then-check ; from the second
            still-pending check onward it escalates to the amber
            re-request hint, which also carries the 2026-07-04
            dead-order EXIT PATH (Sectigo® can leave an order
            permanently unprocessed while answering "processing" -
            observed live on order 3186288898 - and a fresh order
            validates in minutes when everything else is correct).

            THE TWO BLUE NOTICES ("Validation Requested", "Validation
            Pending") ARE HIDDEN while a cooldown notice is up : each
            is ambient context the customer read minutes ago ("wait,
            then check"), and a cooldown is the direct reply to the
            button they just pressed, saying the same thing with the
            real number attached. Stacked, they were two blue boxes
            opening on the same word and closing on the same advice.
            The cooldown is dismissible, so the blue notice returns the
            moment it is closed.

            THE AMBER "Validation Still Pending" NOTICE STAYS beside a
            cooldown notice, deliberately (operator ruling 2026-10-02) :
            it carries different advice - propagation, when to request
            again, the way out of a stalled issuance request - none of
            which a cooldown notice says. */}
        {/* WHICH SENTENCE DEPENDS ON HOW WE GOT HERE (operator
              report, 2026-08-22, from his live run). Both states are
              "pending", but they answer different presses :

                pendingChecks === 0 : a TRIGGER just succeeded, so
                  "Validation Requested" is a true report of what the
                  tool did.
                pendingChecks >= 1  : a read-only CHECK came back
                  pending. Nothing was requested, and the old shared
                  heading told the customer otherwise - he pressed Check
                  Validation Status, read "Trustico® has asked the
                  Certificate Authority to validate this domain", and
                  reasonably concluded the check had submitted a
                  request. It had not. runCheck is strictly read-only ;
                  the words were wrong, not the behaviour. */}
        {checkState === "pending" && pendingChecks === 0 && !cooldownNotice && (
          <Alert
            type="info"
            title="Validation Requested"
            className="mt-6 mb-0"
            live
          >
            Trustico® has asked the Certificate Authority (CA) to validate this
            domain. Their check can take several minutes. Wait a moment, then
            check the validation status to read the result.
          </Alert>
        )}
        {checkState === "pending" && pendingChecks === 1 && !cooldownNotice && (
          <Alert
            type="info"
            title="Validation Pending"
            className="mt-6 mb-0"
            live
          >
            The Certificate Authority (CA) has not confirmed this domain yet.
            Their check can take several minutes. Wait a moment, then check the
            validation status again to read the result.
          </Alert>
        )}
        {/* Reachable ~30 seconds after the trigger since the status
              poll gained its own floor (2026-08-16), while the trigger
              this steers to is refused for five minutes. That tension is
              now VISIBLE rather than explained away : the Request
              Validation button beside this notice carries its own
              countdown, so a customer reading "request validation again"
              can see exactly when that becomes possible. The ten-to-
              fifteen minutes below is the Certificate Authority's own
              cadence between attempts, NOT our five-minute floor - two
              different numbers, and the sentence says which is which. */}
        {checkState === "pending" && pendingChecks >= 2 && (
          <Alert
            type="warning"
            title="Validation Still Pending"
            className="mt-6 mb-0"
            live
          >
            <p>
              This is normal. A new record can take time to reach the
              Certificate Authority (CA), and if a record was already in place
              the old value may still be cached until its original Time To Live
              (TTL) expires. Allow time for the record to propagate, then check
              the validation status again.
            </p>
            <p className="mt-3">
              If it is still not validated, request validation again to start a
              fresh check. The Certificate Authority (CA) generally needs ten to
              fifteen minutes between attempts on the same domain, so a request
              made sooner may simply repeat the last answer. If the domain stays
              pending long after repeated fresh requests, the issuance request
              itself may have stalled - start a new issuance request, then add
              the new validation details it shows you.
            </p>
          </Alert>
        )}

        {/*
          2026-08-13 go-public cleanup (audit : "misleading-failure-state") :
          the trigger cooldown renders as INFORMATION, not failure - the
          red "VALIDATION FAILED" treatment sent customers to fix a
          challenge that was fine. The server message carries the exact
          seconds remaining, so it is surfaced verbatim.
        */}
        {cooldownNotice && (
          <Alert
            type="info"
            title={
              cooldownNotice.code === CHECK_COOLDOWN_CODE
                ? "Status Just Checked"
                : "Validation Already Requested"
            }
            className="mt-6 mb-0"
            live
            onClose={() => setCooldownNotice(null)}
          >
            {/* ONE paragraph, straight from the server (2026-08-16) :
                  the message is now a complete sentence-case sentence
                  carrying the live remaining wait, so a second
                  hand-written line beneath it repeated the same advice
                  in a different register - which is what made this box
                  read as three voices at once. */}
            <p>{cooldownNotice.message}</p>
          </Alert>
        )}

        {/*
          2026-08-13 go-public cleanup (audit : "misleading-guidance") :
          an INVALID authorization is terminal under RFC 8555 §7.1.6 -
          re-checking can never succeed - so its guidance points at the
          only real exit, starting a fresh order, without naming the
          control (see the naming ruling applied across this file). The "correct and check
          again" advice is kept ONLY for a transient CHECK failure
          (runCheck's catch), which genuinely is retryable.

          A FAILED REQUEST IS NOT A FAILED CHECK (operator ruling
          2026-10-02) : when the error came from runTrigger the Alert
          is titled "Validation Request Failed" and shows the failure's
          own sentence alone. The check advice would tell a customer
          whose request never went through that a check had run, and
          send them to correct a record nothing has looked at.
          checkErrorFromTrigger is reset at the top of every runCheck
          and only runCheck reaches "invalid", so it is never set on
          the terminal Alert.
        */}
        {checkError && (
          <Alert
            type="error"
            title={
              checkState === "invalid"
                ? "Domain Validation Failed Permanently"
                : checkErrorFromTrigger
                  ? "Validation Request Failed"
                  : "Validation Failed"
            }
            className="mt-6 mb-0"
            live
            // The terminal invalid Alert is NOT dismissable : with the
            // interactive sequence withdrawn it is this panel's entire
            // remaining interface, and dismissing it would leave an
            // empty panel with no explanation and no exit (2026-08-14
            // go-public review). Transient failures stay dismissable.
            onClose={
              checkState === "invalid"
                ? undefined
                : () => {
                    setCheckError(null);
                    setCheckErrorFromTrigger(false);
                  }
            }
          >
            {checkErrorFromTrigger ? (
              <p>{checkError}</p>
            ) : (
              <>
                <p className="mb-1">{checkError}</p>
                <p>
                  <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                  {checkState === "invalid"
                    ? "The Certificate Authority (CA) has rejected this domain and will not check it again for this issuance request. Start a new issuance request, then add the new validation details it shows you."
                    : "The check did not succeed. You can correct the record or file and check again."}
                </p>
              </>
            )}
          </Alert>
        )}
      </div>
    </div>
  );
}

export default ChallengePanel;
